// Filtro de selección múltiple, buscable, para todos los paneles de filtros.
//
// Sustituye al campo de texto suelto allí donde tiene sentido acumular varias
// condiciones: «estas dos referencias», «estos tres lotes». Dentro de un mismo
// filtro las selecciones se combinan con OR; el AND entre filtros distintos lo
// compone cada pantalla.
//
// DOS MODOS, y la diferencia importa:
//   · CATÁLOGO (`opciones` con contenido): se elige de una lista real, la que
//     sale de los datos ya cargados. Es lo correcto cuando el campo tiene un
//     dominio cerrado —artículos de un pedido, lotes de una producción—.
//   · TEXTO LIBRE (`permitirLibre`): Enter convierte lo escrito en un término.
//     Es lo que necesitan los buscadores globales de las listas de pedidos, que
//     hoy cubren número, cliente, contenedor, BL y artículos a la vez: ahí no
//     hay catálogo posible y sustituirlo por uno perdería la mitad del alcance.
// Los dos se pueden combinar: sugerencias del catálogo y, además, texto libre.
//
// EL CONTROL NO CAMBIA NUNCA DE TAMAÑO. Ni los chips dentro —a la tercera
// selección el input se queda sin sitio— ni colgando debajo, que hacía crecer la
// barra y desplazaba el resto de controles cada vez que se elegía algo. Aquí
// dentro sólo va un contador; el detalle de lo elegido vive en la franja común
// de filtros activos (`BarraFiltrosActivos`), fuera de la toolbar.
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { usePosicionOverlay } from './usePosicionOverlay'
import { partirTerminos, resolverPegado } from '@/utils/filtroTexto'

/** Cuántas opciones se pintan a la vez en el desplegable. */
const MAX_SUGERENCIAS = 200

export interface OpcionMulti {
  valor: string
  /** Lo que identifica la opción; se pinta en monoespaciada. */
  titulo: string
  /** Apoyo para buscar y para leer. */
  detalle?: string
}

interface Props {
  dark: boolean
  /** Catálogo. Puede ir vacío si sólo se quiere texto libre. */
  opciones?: OpcionMulti[]
  seleccion: string[]
  onSeleccion: (valores: string[]) => void
  placeholder: string
  /** Enter añade lo escrito aunque no esté en el catálogo. */
  permitirLibre?: boolean
  /** El título de cada opción en monoespaciada. Cierto por defecto porque casi
   *  todos los catálogos son códigos, donde alinea los dígitos; se apaga cuando
   *  las opciones son nombres (proveedores, secciones) y la mono los afea. */
  titulosMono?: boolean
  /**
   * ¿Cuenta el control como modificado? Decide el resalte del marco y si se pinta
   * el contador. Por defecto, tener algo elegido.
   *
   * Se pasa a mano en los controles que NUNCA están vacíos: el selector de
   * niveles de los totales arranca con un nivel puesto, así que con la regla por
   * defecto saldría siempre resaltado y con un «1» al lado, y el resalte dejaría
   * de querer decir «esto lo has cambiado tú».
   */
  modificado?: boolean
  /** Texto del `title`, para el control entero. */
  title?: string
  className?: string
}

export function MultiFiltro({
  dark, opciones = [], seleccion, onSeleccion, placeholder,
  permitirLibre = false, titulosMono = true, modificado, title, className = '',
}: Props) {
  const { t } = useTranslation()
  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState('')
  const [resaltada, setResaltada] = useState(0)
  const caja = useRef<HTMLDivElement>(null)
  const campo = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const panel = useRef<HTMLUListElement>(null)
  // El desplegable va en un portal con posición fija: dentro del control
  // ensanchaba la página al abrirse cerca del borde derecho.
  const pos = usePosicionOverlay(campo, abierto)

  const coincidencias = useMemo(() => {
    const q = texto.trim().toLowerCase()
    if (!q) return opciones
    // Se busca por el TÍTULO —lo que se ve— y también por el VALOR, que puede
    // no coincidir: en los catálogos de artículo el título va formateado para el
    // rol avp (`QARF…` se enseña como `ARF…`) y el valor sigue siendo el código
    // crudo de la vista. Sin mirar el valor, quien pega la referencia tal cual
    // sale de D365 no encontraría su propia opción.
    return opciones.filter(o =>
      o.titulo.toLowerCase().includes(q)
      || o.valor.toLowerCase().includes(q)
      || (o.detalle ?? '').toLowerCase().includes(q))
  }, [opciones, texto])

  // El desplegable pinta como mucho `MAX_SUGERENCIAS`, y el resto se resume.
  // Sin tope, un catálogo grande —los componentes de todas las producciones de
  // un año son miles— monta miles de nodos en cada pulsación y el campo se
  // queda pensando justo mientras alguien escribe. Nadie recorre mil opciones
  // con la vista: se sigue escribiendo hasta que la que se busca está arriba.
  const filtradas = useMemo(
    () => coincidencias.slice(0, MAX_SUGERENCIAS), [coincidencias])
  const ocultas = coincidencias.length - filtradas.length

  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node
      // El panel vive fuera del árbol del control, así que hay que mirarlo
      // aparte o pulsar una opción cerraría el desplegable antes del clic.
      if (caja.current?.contains(t) || panel.current?.contains(t)) return
      setAbierto(false)
      setTexto('')
    }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])

  function alternar(valor: string) {
    // Volver a pulsar una opción ya elegida la quita: es lo que espera quien la
    // ve marcada. Y así nunca hay duplicados.
    onSeleccion(seleccion.includes(valor)
      ? seleccion.filter(v => v !== valor)
      : [...seleccion, valor])
    setTexto('')
    setResaltada(0)
    input.current?.focus()
  }

  // Pegar una lista añade un término por línea, de golpe.
  //
  // El `<input>` es de una sola línea, así que el navegador colapsa los saltos:
  // sin esto, pegar diez referencias dejaba las diez amontonadas en un único
  // término que no casa con nada. Es el mismo gesto que hace todo el mundo con
  // una columna de Excel, y hasta ahora no funcionaba en ninguna pantalla.
  //
  // Un solo término se deja pasar tal cual: pegar media referencia para seguir
  // escribiéndola es normal, y convertirla en chip lo impediría.
  function pegar(e: React.ClipboardEvent<HTMLInputElement>) {
    const partes = partirTerminos(e.clipboardData.getData('text'))
    if (partes.length < 2) return

    e.preventDefault()
    const nuevos = resolverPegado(partes, opciones, seleccion, permitirLibre)
    if (nuevos.length > 0) onSeleccion([...seleccion, ...nuevos])
    setTexto('')
    setResaltada(0)
  }

  // Cuando se puede añadir texto libre, esa fila se pinta ARRIBA del catálogo.
  // Antes no entraba en la navegación con teclado, así que Enter elegía siempre
  // la primera sugerencia y el término escrito no había manera de añadirlo sin
  // ratón, aunque su fila estuviera resaltada.
  const libre = texto.trim()
  const puedeAnadirLibre = permitirLibre && !!libre
    && !filtradas.some(o => o.titulo.toLowerCase() === libre.toLowerCase())
  const desplazamiento = puedeAnadirLibre ? 1 : 0
  const totalFilas = desplazamiento + filtradas.length

  function teclas(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setAbierto(false); setTexto(''); return }
    if (e.key === 'Tab') { setAbierto(false); return }
    if (e.key === 'Backspace' && !texto && seleccion.length > 0) {
      onSeleccion(seleccion.slice(0, -1))
      return
    }
    if (e.key === 'Enter') {
      if (abierto && puedeAnadirLibre && resaltada === 0) {
        alternar(libre); e.preventDefault(); return
      }
      const sugerida = abierto ? filtradas[resaltada - desplazamiento] : undefined
      if (sugerida) { alternar(sugerida.valor); e.preventDefault(); return }
      // Sin sugerencia bajo el cursor, lo escrito se convierte en término.
      if (permitirLibre && libre) { alternar(libre); e.preventDefault() }
      return
    }
    if (!abierto && e.key === 'ArrowDown') {
      setAbierto(true); setResaltada(0); e.preventDefault(); return
    }
    if (!abierto) return
    if (e.key === 'ArrowDown') {
      setResaltada(i => Math.min(i + 1, totalFilas - 1)); e.preventDefault()
    } else if (e.key === 'ArrowUp') {
      setResaltada(i => Math.max(i - 1, 0)); e.preventDefault()
    }
  }

  const activo = modificado ?? seleccion.length > 0
  const marco = activo
    ? (dark
        ? 'border-sky-500/60 bg-slate-800 ring-1 ring-sky-500/15'
        : 'border-sky-400 bg-sky-50/60 ring-1 ring-sky-300/25')
    : (dark
        ? 'border-white/10 bg-slate-800 focus-within:border-sky-500 hover:border-white/20'
        : 'border-slate-200 bg-white focus-within:border-sky-400 hover:border-slate-300')

  return (
    <div ref={caja} className={`relative ${className}`} title={title}>
      <div ref={campo} className={`flex items-center gap-1.5 min-h-[32px] px-2.5 rounded-lg border transition-colors duration-150 ${marco}`}>
        <svg className={`w-3.5 h-3.5 shrink-0 ${dark ? 'text-slate-500' : 'text-slate-400'}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <circle cx="7" cy="7" r="4.5" /><path d="M10.5 10.5L14 14" />
        </svg>

        {/* Un contador, no las selecciones: el campo tiene que seguir siendo un
            campo por muchas cosas que se elijan. */}
        {activo && (
          <span className={`shrink-0 h-[19px] px-1.5 rounded text-[11px] font-semibold tabular-nums leading-[19px] ${
            dark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700'
          }`}>
            {seleccion.length}
          </span>
        )}

        <input
          ref={input}
          role="combobox"
          aria-expanded={abierto}
          autoComplete="off"
          value={texto}
          placeholder={placeholder}
          onFocus={() => setAbierto(true)}
          onChange={e => { setTexto(e.target.value); setResaltada(0); setAbierto(true) }}
          onKeyDown={teclas}
          onPaste={pegar}
          className={`flex-1 min-w-[70px] h-[30px] bg-transparent outline-none text-[12px] ${
            dark ? 'text-slate-100 placeholder-slate-500' : 'text-slate-800 placeholder-slate-400'
          }`}
        />

        {opciones.length > 0 && (
          <svg
            className={`w-3 h-3 shrink-0 transition-transform duration-150 ${abierto ? 'rotate-180' : ''} ${dark ? 'text-slate-500' : 'text-slate-400'}`}
            viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M4 6.5l4 4 4-4" />
          </svg>
        )}
      </div>


      {abierto && pos && (filtradas.length > 0 || puedeAnadirLibre) && createPortal(
        <ul
          ref={panel}
          role="listbox"
          aria-multiselectable
          style={{
            top: pos.top, left: pos.left, right: pos.right,
            minWidth: pos.minWidth, maxWidth: pos.maxWidth, maxHeight: pos.maxHeight,
          }}
          className={`fixed z-[61] overflow-y-auto scroll-thin rounded-lg border shadow-lg ${
            dark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-white'
          }`}
        >
          {puedeAnadirLibre && (
            <li>
              <button
                type="button"
                onMouseEnter={() => setResaltada(0)}
                onClick={() => alternar(libre)}
                className={`w-full flex items-baseline gap-2 px-2.5 py-2 text-left cursor-pointer transition-colors duration-150 ${
                  resaltada === 0 ? (dark ? 'bg-sky-500/12' : 'bg-sky-50') : ''
                } ${filtradas.length > 0 ? `border-b ${dark ? 'border-white/8' : 'border-slate-200'}` : ''}`}
              >
                <span className="w-3.5 shrink-0 text-sky-500">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M8 3.5v9M3.5 8h9" />
                  </svg>
                </span>
                <span className={`text-[12px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                  {libre}
                </span>
                <span className={`text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {t('common.multiFiltro.anadirTermino')}
                </span>
              </button>
            </li>
          )}

          {filtradas.map((o, i) => {
            const elegida = seleccion.includes(o.valor)
            return (
              <li key={o.valor} role="option" aria-selected={elegida}>
                <button
                  type="button"
                  // El detalle va truncado para que la fila no se desborde: el
                  // título lo devuelve entero al pasar por encima.
                  title={o.detalle ? `${o.titulo} · ${o.detalle}` : o.titulo}
                  onMouseEnter={() => setResaltada(i + desplazamiento)}
                  onClick={() => alternar(o.valor)}
                  className={`w-full flex items-baseline gap-2 px-2.5 py-1.5 text-left cursor-pointer transition-colors duration-150 ${
                    i > 0 ? `border-t ${dark ? 'border-white/5' : 'border-slate-100'}` : ''
                  } ${i + desplazamiento === resaltada ? (dark ? 'bg-sky-500/12' : 'bg-sky-50') : ''}`}
                >
                  <span className={`w-3.5 shrink-0 ${elegida ? 'text-sky-500' : 'opacity-0'}`}>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8.5l3.5 3.5L13 5" />
                    </svg>
                  </span>
                  <span className={`text-[12px] font-semibold shrink-0 ${titulosMono ? 'font-mono' : ''} ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                    {o.titulo}
                  </span>
                  {o.detalle && (
                    <span className={`text-[11px] truncate ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {o.detalle}
                    </span>
                  )}
                </button>
              </li>
            )
          })}

          {ocultas > 0 && (
            <li
              className={`px-2.5 py-1.5 text-[11px] border-t ${
                dark ? 'border-white/8 text-slate-500' : 'border-slate-200 text-slate-400'
              }`}
            >
              {t('common.multiFiltro.masOpciones', { count: ocultas })}
            </li>
          )}
        </ul>,
        document.body,
      )}
    </div>
  )
}

/** ¿La fila casa con alguno de los términos? OR entre términos.
 *
 *  `campos` son los textos de la fila donde buscar; con catálogo se compara por
 *  igualdad y con texto libre por contenido, que es lo que espera cada uno. */
export function casaAlgunTermino(terminos: string[], campos: (string | undefined)[]): boolean {
  if (terminos.length === 0) return true
  const texto = campos.filter(Boolean).join(' ').toLowerCase()
  return terminos.some(term => texto.includes(term.toLowerCase()))
}
